import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import App from "./App.tsx";
import "./index.css";
import { cleanupOldDrafts } from "./hooks/useFormAutosave";

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Global unhandled promise rejection handler
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
  Sentry.captureException(event.reason);

  if (
    event.reason?.message?.includes("fetch") ||
    event.reason?.message?.includes("network") ||
    event.reason?.message?.includes("Failed to fetch")
  ) {
    toast.error("Connection issue — check your internet and try again");
  }
});

// Online/offline detection
window.addEventListener("offline", () => {
  toast.error("You're offline — changes won't save until you reconnect", {
    duration: Infinity,
    id: "offline-toast",
  });
});

window.addEventListener("online", () => {
  toast.dismiss("offline-toast");
  toast.success("You're back online");
});

// Clean up old drafts on app startup
cleanupOldDrafts();

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-4">Please refresh the page to continue.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            Refresh page
          </button>
        </div>
      </div>
    }
  >
    <App />
  </Sentry.ErrorBoundary>
);
